/**
 * A board column.
 *
 * ## Reordering columns is a menu, not a drag
 *
 * Cards are dragged; columns are moved with "Move left" / "Move right" in the
 * column menu. That asymmetry is deliberate.
 *
 * The board scrolls horizontally, and a horizontal drag on a horizontally
 * scrolling surface is an interaction with no unambiguous reading: a drag that
 * starts on a column header and moves left could mean "reorder this column" or
 * "scroll the board", and the two cannot be distinguished until the gesture is
 * under way. Every board product that supports both ends up with an activation
 * delay or a modifier key, and both are worse than a menu.
 *
 * The menu is also the only one of the two that is reachable by keyboard without
 * a custom roving-focus implementation, and it is unambiguous to a screen reader.
 * `reorderColumn` still goes through the same Durable Object mutation as a drag
 * would, so nothing about the data path is different - only the gesture.
 *
 * ## The drop target is the whole column, not the gap between cards
 *
 * `useDroppable` is attached to the column body, so a drag that ends anywhere in
 * an empty column lands the card there. Dropping only between cards would make an
 * empty column impossible to target, which is the state a column is in exactly
 * when a user most wants to move a card into it.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import type { Card, Column, PublicUser } from '@slate/shared';
import { CardTile } from './CardTile.js';
import { Icon, Modal, ghostButtonClass, inputClass, primaryButtonClass, dangerButtonClass } from './ui.js';

/** The droppable id for a column. Namespaced so a column id can never collide
 * with a card id in the drag context - both are UUIDs, so a collision is
 * vanishingly unlikely and a silent wrong drop is not a failure mode worth
 * leaving to probability. */
export function columnDropId(columnId: string): string {
  return `column:${columnId}`;
}

export function isColumnDropId(id: string): boolean {
  return id.startsWith('column:');
}

export function columnIdFromDropId(id: string): string {
  return id.slice('column:'.length);
}

export interface ColumnViewProps {
  column: Column;
  index: number;
  columnCount: number;
  cards: Card[];
  members: Map<string, PublicUser>;
  pendingCards: Set<string>;
  typingByCard: Record<string, string[]>;
  onOpenCard(cardId: string): void;
  onAddCard(columnId: string, title: string): void;
  onRenameColumn(columnId: string, name: string): void;
  onMoveColumn(columnId: string, index: number): void;
  onDeleteColumn(
    columnId: string,
    strategy?: { kind: 'archive' } | { kind: 'move'; targetColumnId: string },
  ): Promise<{ ok: true } | { ok: false; cardCount: number; message: string }>;
  /** Every other column, offered as a destination when a non-empty column is
   * deleted. */
  otherColumns: { id: string; name: string }[];
}

export function ColumnView({
  column,
  index,
  columnCount,
  cards,
  members,
  pendingCards,
  typingByCard,
  onOpenCard,
  onAddCard,
  onRenameColumn,
  onMoveColumn,
  onDeleteColumn,
  otherColumns,
}: ColumnViewProps): ReactNode {
  const { setNodeRef, isOver } = useDroppable({ id: columnDropId(column.id), data: { type: 'column', columnId: column.id } });

  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [deleteState, setDeleteState] = useState<
    { kind: 'idle' } | { kind: 'confirm'; cardCount: number } | { kind: 'refused'; cardCount: number; message: string }
  >({ kind: 'idle' });

  const composerRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (composing) composerRef.current?.focus();
  }, [composing]);

  // Dismiss the menu on an outside click or Escape. A menu that stays open until
  // its own button is pressed again is a menu that covers the board.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (menuRef.current !== null && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  function submitDraft(): void {
    const title = draft.trim();
    if (title === '') {
      setComposing(false);
      setDraft('');
      return;
    }
    onAddCard(column.id, title);
    // The composer stays open so a user can add several cards in a row, which is
    // what someone setting up a board actually does.
    setDraft('');
  }

  async function requestDelete(strategy?: { kind: 'archive' } | { kind: 'move'; targetColumnId: string }): Promise<void> {
    const result = await onDeleteColumn(column.id, strategy);
    if (result.ok) {
      setDeleteState({ kind: 'idle' });
      return;
    }
    setDeleteState({ kind: 'refused', cardCount: result.cardCount, message: result.message });
  }

  return (
    <section
      aria-label={`${column.name}, ${cards.length} ${cards.length === 1 ? 'card' : 'cards'}`}
      className="snap-start-column flex w-[17.5rem] shrink-0 flex-col rounded-xl bg-sunken"
    >
      <header className="flex items-center gap-1 px-2.5 pt-2.5 pb-1.5">
        {renaming ? (
          <input
            autoFocus
            defaultValue={column.name}
            className={inputClass}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (value !== '' && value !== column.name) onRenameColumn(column.id, value);
              setRenaming(false);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.currentTarget.blur();
              if (event.key === 'Escape') setRenaming(false);
            }}
          />
        ) : (
          <>
            <h2 className="min-w-0 flex-1 truncate text-xs font-semibold tracking-wide text-ink-muted uppercase">
              {column.name}
            </h2>
            <span className="rounded-full bg-surface px-1.5 text-[11px] tabular-nums text-ink-subtle">{cards.length}</span>
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                aria-label={`Column options for ${column.name}`}
                className="rounded p-0.5 text-ink-subtle hover:bg-surface hover:text-ink"
              >
                <Icon name="chevron-down" size={14} />
              </button>
              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-30 mt-1 w-44 rounded-lg border border-border bg-raised py-1 shadow-lift"
                >
                  <MenuItem
                    onClick={() => {
                      setRenaming(true);
                      setMenuOpen(false);
                    }}
                  >
                    Rename
                  </MenuItem>
                  <MenuItem
                    disabled={index === 0}
                    onClick={() => {
                      onMoveColumn(column.id, index - 1);
                      setMenuOpen(false);
                    }}
                  >
                    Move left
                  </MenuItem>
                  <MenuItem
                    disabled={index === columnCount - 1}
                    onClick={() => {
                      onMoveColumn(column.id, index + 1);
                      setMenuOpen(false);
                    }}
                  >
                    Move right
                  </MenuItem>
                  <MenuItem
                    tone="danger"
                    onClick={() => {
                      setDeleteState({ kind: 'confirm', cardCount: cards.length });
                      setMenuOpen(false);
                    }}
                  >
                    Delete column
                  </MenuItem>
                </div>
              )}
            </div>
          </>
        )}
      </header>

      <div
        ref={setNodeRef}
        className={`scroll-slim flex min-h-[4rem] flex-1 flex-col gap-1.5 overflow-y-auto rounded-lg px-2 pt-1 pb-1 transition-colors ${
          isOver ? 'bg-accent-soft' : ''
        }`}
      >
        <SortableContext items={cards.map((card) => card.id)} strategy={verticalListSortingStrategy}>
          {cards.map((card) => (
            <CardTile
              key={card.id}
              card={card}
              assignee={card.assigneeId === null ? null : (members.get(card.assigneeId) ?? null)}
              isPending={pendingCards.has(card.id)}
              typingNames={typingByCard[card.id] ?? []}
              onOpen={onOpenCard}
            />
          ))}
        </SortableContext>

        {cards.length === 0 && !composing && (
          // A visible empty state rather than a blank strip. It is also the drop
          // hint, which is why it is inside the droppable rather than above it.
          <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-xs text-ink-subtle">
            Drop a card here
          </p>
        )}
      </div>

      <footer className="p-2 pt-1">
        {composing ? (
          <div className="rounded-lg border border-border bg-surface p-2">
            <textarea
              ref={composerRef}
              value={draft}
              rows={2}
              placeholder="Card title"
              aria-label={`New card in ${column.name}`}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  submitDraft();
                }
                if (event.key === 'Escape') {
                  setComposing(false);
                  setDraft('');
                }
              }}
              className="w-full resize-none bg-transparent text-sm text-ink placeholder:text-ink-subtle"
            />
            <div className="mt-1.5 flex items-center gap-2">
              <button type="button" onClick={submitDraft} className={primaryButtonClass}>
                Add card
              </button>
              <button
                type="button"
                onClick={() => {
                  setComposing(false);
                  setDraft('');
                }}
                className={ghostButtonClass}
              >
                Cancel
              </button>
              <span className="ml-auto text-[11px] text-ink-subtle">Enter to add</span>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setComposing(true)}
            className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-ink-muted hover:bg-surface hover:text-ink"
          >
            <Icon name="plus" size={14} />
            Add a card
          </button>
        )}
      </footer>

      {deleteState.kind === 'confirm' && (
        <Modal title={`Delete ${column.name}`} onClose={() => setDeleteState({ kind: 'idle' })}>
          {deleteState.cardCount === 0 ? (
            <>
              <p className="text-sm text-ink-muted">
                This column is empty. Deleting it cannot be undone.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button type="button" className={ghostButtonClass} onClick={() => setDeleteState({ kind: 'idle' })}>
                  Cancel
                </button>
                <button type="button" className={dangerButtonClass} onClick={() => void requestDelete()}>
                  Delete column
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-ink-muted">
                This column holds {deleteState.cardCount} {deleteState.cardCount === 1 ? 'card' : 'cards'}. Choose what
                should happen to them.
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <button
                  type="button"
                  className={ghostButtonClass}
                  onClick={() => void requestDelete({ kind: 'archive' })}
                >
                  Archive the cards, then delete the column
                </button>
                {otherColumns.length > 0 && (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      const data = new FormData(event.currentTarget);
                      const target = String(data.get('targetColumnId') ?? '');
                      if (target !== '') void requestDelete({ kind: 'move', targetColumnId: target });
                    }}
                    className="flex items-center gap-2"
                  >
                    <label htmlFor={`move-target-${column.id}`} className="text-sm text-ink-muted">
                      Move them to
                    </label>
                    <select
                      id={`move-target-${column.id}`}
                      name="targetColumnId"
                      className={inputClass}
                      defaultValue={otherColumns[0]?.id ?? ''}
                    >
                      {otherColumns.map((other) => (
                        <option key={other.id} value={other.id}>
                          {other.name}
                        </option>
                      ))}
                    </select>
                    <button type="submit" className={primaryButtonClass}>
                      Move
                    </button>
                  </form>
                )}
              </div>
              <div className="mt-4 flex justify-end">
                <button type="button" className={ghostButtonClass} onClick={() => setDeleteState({ kind: 'idle' })}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {deleteState.kind === 'refused' && (
        <Modal title="Cards would be lost" onClose={() => setDeleteState({ kind: 'idle' })}>
          <p className="text-sm text-ink-muted">{deleteState.message}</p>
          <p className="mt-2 text-sm text-ink-muted">
            {deleteState.cardCount} {deleteState.cardCount === 1 ? 'card is' : 'cards are'} still in this column. Choose
            what should happen to them, or cancel and move them yourself.
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <button type="button" className={ghostButtonClass} onClick={() => void requestDelete({ kind: 'archive' })}>
              Archive the cards, then delete the column
            </button>
            {otherColumns.length > 0 && (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  const data = new FormData(event.currentTarget);
                  const target = String(data.get('targetColumnId') ?? '');
                  if (target !== '') void requestDelete({ kind: 'move', targetColumnId: target });
                }}
                className="flex items-center gap-2"
              >
                <label htmlFor={`move-target-refused-${column.id}`} className="text-sm text-ink-muted">
                  Move them to
                </label>
                <select
                  id={`move-target-refused-${column.id}`}
                  name="targetColumnId"
                  className={inputClass}
                  defaultValue={otherColumns[0]?.id ?? ''}
                >
                  {otherColumns.map((other) => (
                    <option key={other.id} value={other.id}>
                      {other.name}
                    </option>
                  ))}
                </select>
                <button type="submit" className={primaryButtonClass}>
                  Move
                </button>
              </form>
            )}
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" className={ghostButtonClass} onClick={() => setDeleteState({ kind: 'idle' })}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

function MenuItem({
  children,
  onClick,
  disabled = false,
  tone = 'default',
}: {
  children: ReactNode;
  onClick(): void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}): ReactNode {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={`block w-full px-3 py-1.5 text-left text-sm disabled:opacity-40 ${
        tone === 'danger' ? 'text-danger hover:bg-danger-soft' : 'text-ink hover:bg-sunken'
      }`}
    >
      {children}
    </button>
  );
}
